import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { CompleteCourseDataAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';

interface CourseCard {
  id: string;
  title: string;
  classroom: string;
  status: 'published' | 'draft';
}

@Component({
  selector: 'app-admin-courses',
  standalone: true,
  imports: [CommonModule,MatTooltipModule,HasPermissionDirective],
  templateUrl: './admin-courses.component.html',
  styleUrl: './admin-courses.component.scss'
})
export class AdminCoursesComponent {

  courses: CourseCard[] = [];
  loading = false;
  page = 1;
  readonly limit = 10;
  totalRecords = 0;

  private readonly classroomId = environment.classroomId;
  private destroy$ = new Subject<void>();
  subscription = new Subscription();
  openMenuId: string | null = null;
  PermissionForStudents=PermissionForStudents;

  constructor(
    private courseService: CourseManagementService,
    private router: Router,
    private _toastr: ToastrService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadAICourses();
  }

  get totalCourses(): number {
    return this.totalRecords;
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.limit);
  }

  get visiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.page - 1);
    const end = Math.min(this.totalPages, start + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  loadAICourses(): void {
    this.loading = true;

    this.courseService
      .getCourses(this.classroomId, this.page, this.limit, 'created_at', 'desc', 'all')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;

          if (!res?.Success || !res.Data) {
            this.resetData();
            this._toastr.warning(res?.Message || 'No courses found', 'Info');
            return;
          }

          const apiCourses: CompleteCourseDataAI[] = res.Data.data ?? [];

          this.courses = apiCourses.map(course => ({
            id: course.course_id ?? '',
            title: course.course_title ?? 'Untitled Course',
            classroom: String(course.classroom_id ?? ''),
            status: course.status === 'Published' ? 'published' : 'draft',
          }));

          this.totalRecords = res.Data.total ?? 0;
        },
        error: (err) => {
          this.loading = false;
          this.resetData();
          this._toastr.error('Failed to load courses', 'Server Error');
        },
      });
  }

  changePage(p: number): void {
    if (p < 1 || p > this.totalPages || p === this.page) return;
    this.page = p;
    this.loadAICourses();
  }

  private resetData(): void {
    this.courses = [];
    this.totalRecords = 0;
  }

  onAddCourse(): void {
    this.router.navigate(['/admin-student-platform/course-setup']);
  }

  onViewCourse(course: CourseCard): void {
    this.router.navigate([
      '/admin-student-platform/admin-review',this.classroomId,
      course.id
    ]);
  }

  toggleMenu(courseId: string): void {
    this.openMenuId = this.openMenuId === courseId ? null : courseId;
  }

  onEditCourse(course: CourseCard): void {
    this.openMenuId = null;
    this.router.navigate([
	'/admin-student-platform/admin-review',this.classroomId ,course.id
    ]);
  }

  onDeleteCourse(course: CourseCard): void {
    this.openMenuId = null;
    this.openDeleteCourseDialogBox(course.id);
  }

  openDeleteCourseDialogBox(courseId: string) {
    this.dialog.open(DeleteDialogBoxComponent, {
      width: '360px',
      data: { courseId }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.deleteCoursesById(courseId);
      }
    });
  }

  deleteCoursesById(courseId: string): void {
    this.subscription.add(
      this.courseService.deleteAdminCourseById(courseId).subscribe({
        next: (res: any) => {
          if (res?.Success) {
            this._toastr.success('Course deleted successfully');
            this.loadAICourses();
          } else {
            this._toastr.error(res?.Message || 'Failed to delete course');
          }
        },
        error: () => {
          this._toastr.error('Server error while deleting course');
        }
      })
    );
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.openMenuId = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
